<template>
  <transition name="spotlight-fade">
    <div
      v-if="isOpen"
      class="spotlight"
      role="dialog"
      aria-modal="true"
      aria-label="Search pages"
      @mousedown.self="close"
    >
      <div class="spotlight__panel" role="combobox" aria-expanded="true" aria-haspopup="listbox">
        <div class="spotlight__search">
          <svg class="spotlight__search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="16.6" y1="16.6" x2="21" y2="21"/>
          </svg>
          <input
            ref="input"
            v-model="query"
            type="text"
            class="spotlight__input"
            placeholder="Search pages…"
            autocomplete="off"
            spellcheck="false"
            aria-autocomplete="list"
            aria-controls="spotlight-results"
            :aria-activedescendant="activeRow ? `spotlight-row-${activeIndex}` : null"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="openActive"
            @keydown.esc.prevent="close"
            @keydown.home="onHome"
            @keydown.end="onEnd"
          >
          <button
            v-if="query"
            type="button"
            class="spotlight__clear"
            aria-label="Clear search"
            @click="clearQuery"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
              stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18"/>
              <line x1="18" y1="6" x2="6" y2="18"/>
            </svg>
          </button>
          <kbd v-else class="spotlight__hint-key">esc</kbd>
        </div>

        <div v-if="rows.length" id="spotlight-results" ref="list" class="spotlight__results" role="listbox">
          <template v-for="(row, index) in rows">
            <div v-if="row.type === 'header'" :key="`h-${index}`" class="spotlight__group">
              {{ row.label }}
            </div>
            <button
              v-else
              :id="`spotlight-row-${index}`"
              :key="`r-${row.entry.id}`"
              type="button"
              role="option"
              :aria-selected="index === activeIndex"
              :class="['spotlight__row', { 'is-active': index === activeIndex }]"
              @mousemove="activeIndex = index"
              @click="go(row.entry)"
            >
              <span class="spotlight__row-icon">
                <CIcon :name="row.entry.icon" size="custom"/>
              </span>
              <span class="spotlight__row-body">
                <span class="spotlight__row-title">
                  <span
                    v-for="(part, partIndex) in highlight(row.entry.title)"
                    :key="partIndex"
                    :class="{ 'spotlight__mark': part.hit }"
                  >{{ part.text }}</span>
                </span>
                <span v-if="breadcrumb(row.entry)" class="spotlight__row-path">{{ breadcrumb(row.entry) }}</span>
              </span>
              <span v-if="row.badge" class="spotlight__row-badge">{{ row.badge }}</span>
              <span class="spotlight__row-enter" aria-hidden="true">↵</span>
            </button>
          </template>
        </div>

        <div v-else class="spotlight__empty">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
            stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="16.6" y1="16.6" x2="21" y2="21"/>
          </svg>
          <p class="spotlight__empty-title">No pages match “{{ query }}”</p>
          <p class="spotlight__empty-hint">Try a shorter word, or what the page is about — “cod”, “payroll”, “discount”.</p>
        </div>

        <div class="spotlight__footer">
          <span class="spotlight__legend"><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span class="spotlight__legend"><kbd>↵</kbd> open</span>
          <span class="spotlight__legend"><kbd>esc</kbd> close</span>
          <span class="spotlight__count">{{ resultCount }} {{ resultCount === 1 ? 'page' : 'pages' }}</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { buildCatalog } from '@/utils/spotlightCatalog'
import { searchCatalog, highlightTitle } from '@/utils/spotlightSearch'
import { getRecentPaths, getUsageScore } from '@/utils/spotlightUsage'
import { isGateOpen } from '@/utils/profileCompletion'

const MAX_RESULTS = 30
const MAX_RECENT_ROWS = 5
const MAX_SUGGESTED_ROWS = 6

export default {
  name: 'AppSpotlight',
  data () {
    return {
      isOpen: false,
      query: '',
      activeIndex: 0,
      // Catalog entries are frozen at build time, so holding them in a data
      // property costs nothing — Vue skips observing them.
      entries: [],
      boosts: {},
      recent: [],
      suggested: [],
    }
  },
  computed: {
    results () {
      if (!this.query.trim()) {
        return []
      }
      return searchCatalog(this.entries, this.query, this.boosts, MAX_RESULTS)
    },
    // One flat list of headers + options so arrow keys only ever deal with
    // indices, and the template never needs nested loops.
    rows () {
      const rows = []

      if (this.query.trim()) {
        this.results.forEach((entry) => rows.push({ type: 'option', entry }))
        return rows
      }

      if (this.recent.length) {
        rows.push({ type: 'header', label: 'Recent' })
        this.recent.forEach((entry) => rows.push({ type: 'option', entry, badge: entry.section }))
      }

      if (this.suggested.length) {
        rows.push({ type: 'header', label: this.recent.length ? 'Suggested for you' : 'Jump to' })
        this.suggested.forEach((entry) => rows.push({ type: 'option', entry, badge: entry.section }))
      }

      return rows
    },
    optionIndexes () {
      const indexes = []
      this.rows.forEach((row, index) => {
        if (row.type === 'option') {
          indexes.push(index)
        }
      })
      return indexes
    },
    activeRow () {
      const row = this.rows[this.activeIndex]
      return row && row.type === 'option' ? row : null
    },
    resultCount () {
      return this.optionIndexes.length
    },
  },
  watch: {
    query () {
      this.activeIndex = this.optionIndexes.length ? this.optionIndexes[0] : -1
    },
  },
  mounted () {
    document.addEventListener('keydown', this.onGlobalKeydown)
    this.$root.$on('spotlight:open', this.open)

    // Warm the catalog while the browser is idle so the very first ⌘K has
    // nothing left to compute.
    this.warmTimer = window.setTimeout(this.refreshCatalog, 1500)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onGlobalKeydown)
    this.$root.$off('spotlight:open', this.open)
    window.clearTimeout(this.warmTimer)
  },
  methods: {
    onGlobalKeydown (event) {
      const key = (event.key || '').toLowerCase()

      if (key === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey) {
        event.preventDefault()
        this.toggle()
        return
      }

      if (key === 'escape' && this.isOpen) {
        event.preventDefault()
        this.close()
      }
    },
    toggle () {
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
    },
    open () {
      // The profile gate is a hard wall — nothing should let a user navigate
      // out from under it.
      if (this.isOpen || isGateOpen()) {
        return
      }

      this.refreshCatalog()
      this.query = ''
      this.isOpen = true
      this.activeIndex = this.optionIndexes.length ? this.optionIndexes[0] : -1

      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.focus()
        }
      })
    },
    close () {
      if (!this.isOpen) {
        return
      }
      this.isOpen = false
      this.query = ''
    },
    refreshCatalog () {
      const catalog = buildCatalog({
        router: this.$router,
        activeOrganisationId: this.$store.state.activeOrganisationId,
        permissions: this.$store.state.permissions,
        can: (permission) => this.$store.getters.can(permission),
        userId: this.currentUserId(),
      })

      this.entries = catalog.entries
      this.boosts = this.buildBoosts(catalog.entries)
      this.buildRecommendations(catalog)
    },
    buildBoosts (entries) {
      const boosts = {}
      entries.forEach((entry) => {
        const score = getUsageScore(entry.path)
        if (score) {
          boosts[entry.path] = score
        }
      })
      return boosts
    },
    // Recommendations: where you just were, then where you usually go. With no
    // history yet, "usually" falls back to the curated sidebar order, so a
    // brand-new user still gets a useful list instead of an empty panel.
    buildRecommendations (catalog) {
      const currentPath = this.$route ? this.$route.path : ''
      const used = new Set([currentPath])

      const recent = []
      getRecentPaths().forEach((path) => {
        if (recent.length >= MAX_RECENT_ROWS || used.has(path)) {
          return
        }
        const entry = catalog.byPath.get(path)
        if (entry) {
          used.add(path)
          recent.push(entry)
        }
      })

      const ranked = catalog.entries
        .filter((entry) => !used.has(entry.path) && this.boosts[entry.path])
        .sort((a, b) => this.boosts[b.path] - this.boosts[a.path])

      const suggested = ranked.slice(0, MAX_SUGGESTED_ROWS)
      suggested.forEach((entry) => used.add(entry.path))

      if (suggested.length < MAX_SUGGESTED_ROWS) {
        catalog.entries.forEach((entry) => {
          if (suggested.length >= MAX_SUGGESTED_ROWS || used.has(entry.path) || !entry.inSidebar) {
            return
          }
          used.add(entry.path)
          suggested.push(entry)
        })
      }

      this.recent = recent
      this.suggested = suggested
    },
    currentUserId () {
      try {
        const raw = localStorage.getItem('auth_user')
        const user = raw ? JSON.parse(raw) : null
        const id = user ? Number.parseInt(user.id, 10) : NaN
        return Number.isInteger(id) ? id : null
      } catch (error) {
        return null
      }
    },
    move (delta) {
      const indexes = this.optionIndexes

      if (!indexes.length) {
        return
      }

      const position = indexes.indexOf(this.activeIndex)
      const next = position === -1
        ? indexes[0]
        : indexes[(position + delta + indexes.length) % indexes.length]

      this.activeIndex = next
      this.scrollActiveIntoView()
    },
    onHome (event) {
      if (!this.optionIndexes.length) {
        return
      }
      event.preventDefault()
      this.activeIndex = this.optionIndexes[0]
      this.scrollActiveIntoView()
    },
    onEnd (event) {
      if (!this.optionIndexes.length) {
        return
      }
      event.preventDefault()
      this.activeIndex = this.optionIndexes[this.optionIndexes.length - 1]
      this.scrollActiveIntoView()
    },
    scrollActiveIntoView () {
      this.$nextTick(() => {
        const list = this.$refs.list
        if (!list) {
          return
        }
        const row = list.querySelector('.spotlight__row.is-active')
        if (row && row.scrollIntoView) {
          row.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    openActive () {
      if (this.activeRow) {
        this.go(this.activeRow.entry)
      }
    },
    go (entry) {
      this.close()

      const current = this.$route ? this.$route.fullPath : ''
      if (current === entry.target) {
        return
      }

      // Guard rejections (a stale catalog entry the guard now bounces) and
      // duplicate-navigation errors are both non-events for the user.
      const navigation = this.$router.push(entry.target)
      if (navigation && typeof navigation.catch === 'function') {
        navigation.catch(() => {})
      }
    },
    clearQuery () {
      this.query = ''
      if (this.$refs.input) {
        this.$refs.input.focus()
      }
    },
    highlight (title) {
      return highlightTitle(title, this.query)
    },
    breadcrumb (entry) {
      if (!this.query.trim()) {
        return ''
      }
      return [entry.section, entry.group].filter(Boolean).join(' › ')
    },
  },
}
</script>

<style scoped>
/* Sits above all app chrome but below the profile gate (9990), the toast
   stack (9999) and the session-expiry overlay (99999). */
.spotlight {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9985;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 1rem 2rem;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.spotlight__panel {
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  max-height: min(560px, 72vh);
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

/* ── Search row ──────────────────────────────────────────────────── */
.spotlight__search {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 0.95rem;
  height: 54px;
  flex: 0 0 auto;
  border-bottom: 1px solid #eef1f7;
}

.spotlight__search-icon {
  color: #94a3b8;
  flex: 0 0 auto;
}

.spotlight__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  background: transparent;
  font-size: 0.98rem;
  font-weight: 500;
  color: #1f2a44;
  padding: 0;
}

.spotlight__input::placeholder {
  color: #9aa3b2;
  font-weight: 400;
}

.spotlight__input:focus {
  outline: none;
}

.spotlight__clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.spotlight__clear:hover {
  background: #e2e8f0;
  color: #334155;
}

.spotlight__clear:focus {
  outline: none;
}

.spotlight__clear::-moz-focus-inner {
  border: 0;
}

.spotlight__clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.spotlight__hint-key {
  flex: 0 0 auto;
  padding: 0.12rem 0.4rem;
  border: 1px solid #e5e9f2;
  border-bottom-width: 2px;
  border-radius: 5px;
  background: #f8fafc;
  font-family: inherit;
  font-size: 0.66rem;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.03em;
}

/* ── Results ─────────────────────────────────────────────────────── */
.spotlight__results {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.35rem 0.4rem 0.5rem;
}

.spotlight__group {
  padding: 0.6rem 0.6rem 0.3rem;
  font-size: 0.66rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.spotlight__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.5rem 0.6rem;
  border: 0;
  border-radius: 9px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.spotlight__row:focus {
  outline: none;
}

.spotlight__row::-moz-focus-inner {
  border: 0;
}

/* The active row is tinted, not gradient-filled — the brand gradient stays
   reserved for primary actions. */
.spotlight__row.is-active {
  background: #f2fbf7;
}

.spotlight__row.is-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.spotlight__row-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  transition: background 0.12s ease, color 0.12s ease;
}

.spotlight__row.is-active .spotlight__row-icon {
  background: rgba(22, 160, 122, 0.14);
  color: #0e5b46;
}

/* CIcon's `custom` size hands sizing to CSS; ::v-deep because the svg is the
   child component's root. */
.spotlight__row-icon ::v-deep .c-icon {
  width: 16px;
  height: 16px;
}

.spotlight__row-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.spotlight__row-title {
  font-size: 0.87rem;
  font-weight: 600;
  color: #1f2a44;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spotlight__mark {
  color: #0e5b46;
  background: rgba(22, 160, 122, 0.16);
  border-radius: 3px;
}

.spotlight__row-path {
  font-size: 0.7rem;
  font-weight: 500;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spotlight__row-badge {
  flex: 0 0 auto;
  padding: 0.14rem 0.45rem;
  border-radius: 999px;
  background: #f1f5f9;
  font-size: 0.66rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

/* The neutral pill vanishes against the active row's tint — swap it for a
   white chip so the section stays readable as you arrow down the list. */
.spotlight__row.is-active .spotlight__row-badge {
  background: #ffffff;
  color: #0e5b46;
}

.spotlight__row-enter {
  flex: 0 0 auto;
  width: 14px;
  font-size: 0.8rem;
  color: #16a07a;
  opacity: 0;
}

.spotlight__row.is-active .spotlight__row-enter {
  opacity: 1;
}

/* ── Empty state ─────────────────────────────────────────────────── */
.spotlight__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 2.2rem 1.5rem;
  color: #cbd5e1;
  text-align: center;
}

.spotlight__empty-title {
  margin: 0.4rem 0 0;
  font-size: 0.86rem;
  font-weight: 600;
  color: #475569;
}

.spotlight__empty-hint {
  margin: 0;
  font-size: 0.76rem;
  color: #94a3b8;
}

/* ── Footer ──────────────────────────────────────────────────────── */
.spotlight__footer {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.5rem 0.95rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
}

.spotlight__legend {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.68rem;
  font-weight: 500;
  color: #94a3b8;
}

.spotlight__legend kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  padding: 0 0.24rem;
  border: 1px solid #e5e9f2;
  border-radius: 4px;
  background: #ffffff;
  font-family: inherit;
  font-size: 0.64rem;
  font-weight: 600;
  color: #64748b;
}

.spotlight__count {
  margin-left: auto;
  font-size: 0.68rem;
  font-weight: 600;
  color: #94a3b8;
}

/* ── Transition ──────────────────────────────────────────────────── */
.spotlight-fade-enter-active,
.spotlight-fade-leave-active {
  transition: opacity 0.13s ease;
}

.spotlight-fade-enter-active .spotlight__panel {
  transition: transform 0.15s cubic-bezier(0.2, 0.9, 0.3, 1), opacity 0.15s ease;
}

.spotlight-fade-enter,
.spotlight-fade-leave-to {
  opacity: 0;
}

.spotlight-fade-enter .spotlight__panel {
  transform: translateY(-8px) scale(0.985);
  opacity: 0;
}

@media (max-width: 575px) {
  .spotlight {
    padding: 7vh 0.7rem 1rem;
  }

  .spotlight__row-badge {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spotlight-fade-enter-active,
  .spotlight-fade-leave-active,
  .spotlight-fade-enter-active .spotlight__panel {
    transition: none;
  }
}
</style>
